import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  Select,
  Autocomplete,
  MenuItem,
  FormControl,
  Chip,
  Typography,
  Paper,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller, useWatch, setValue } from "react-hook-form";
import { useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";
import CustomSelect from "../../../components/common/CustomSelect";
import Spinner from "../../../components/common/Spinner";
import ApiCallService from "../../../Service/ApiCallService";
import ReactTable from "../../../components/common/ReactTable";
import CustomButton from "../../../components/common/CustomButton";
import { usePermission } from "../../../permissions/usePermission";
import {
  canOpenNetworkMemberProfile,
  fetchIsNecCouncilMember,
  NEC_COUNCIL_NAME,
} from "../../../utils/necMembership";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import { InfoOutlined } from "@mui/icons-material";
import HistoryIcon from "@mui/icons-material/History";

const ChapterDetailsPage = ({ isDastakChapter = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const theme = useTheme();
  const { canByKey, isSuperAdmin } = usePermission();
  const userDetails = useSelector((state) => state.user.userDetails);
  const canCreateChapter = canByKey("CHAPTER_MANAGEMENT_CHAPTERS_CREATE");

  const canEditChapter = canByKey("CHAPTER_MANAGEMENT_CHAPTERS_EDIT");
  const canDeleteChapter = canByKey("CHAPTER_MANAGEMENT_CHAPTERS_DELETE");
  const canAssignMemberToChapter = canByKey(
    "CHAPTER_MANAGEMENT_CHAPTER_MEMBERS_ASSIGN",
  );

  const canApplyChapterChanges = canCreateChapter || canEditChapter;
  const isEditPath = location.pathname.endsWith("/edit");
  const mode = !id
    ? "create"
    : location.state?.isEdit || isEditPath
      ? "edit"
      : "view";
  const isCreating = mode === "create";
  const isEditing = mode === "edit";
  const isViewing = mode === "view";
  const isPreApprovalFlow =
    location.pathname.includes("/chapter/verify/") ||
    location.state?.source === "chapterRequest";
  const isMemberOnlyReview = Boolean(location.state?.memberOnlyReview);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const [showAddMemberTable, setShowAddMemberTable] = useState(false);
  const [selectedUserForMember, setSelectedUserForMember] = useState(null);
  const [userRoleSelections, setUserRoleSelections] = useState({});
  const [chapterMembersLocal, setChapterMembersLocal] = useState([]);
  const [necMembers, setNecMembers] = useState([]);

  const [users, setUsers] = useState([]);
  const [chapterMembers, setChapterMembers] = useState([]);
  const [chapterRoles, setChapterRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savedData, setSavedData] = useState(null);
  const [states, setStates] = useState([]);
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [hasShownPendingWarning, setHasShownPendingWarning] = useState(false);
  const [roleAvailability, setRoleAvailability] = useState({});
  const [pendingAddRoles, setPendingAddRoles] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userScope, setUserScope] = useState("district");
  const [showCreateMemberSection, setShowCreateMemberSection] = useState(false);
  const [selectedCreateMembers, setSelectedCreateMembers] = useState([]);
  const [usersByLocation, setUsersByLocation] = useState([]);
  const [chapterEditDiffs, setChapterEditDiffs] = useState([]);
  const [isEditRequestView, setIsEditRequestView] = useState(false);
  const [isNecCouncilMember, setIsNecCouncilMember] = useState(false);
  const [dastakDocument, setDastakDocument] = useState(null);
  const dastakDocumentInputRef = useRef(null);
  const [dastakDocumentPreviewOpen, setDastakDocumentPreviewOpen] =
    useState(false);
  const dastakDocumentPreviewUrl = useMemo(
    () => (dastakDocument ? URL.createObjectURL(dastakDocument) : ""),
    [dastakDocument],
  );
  const [preApprovalLiveChapterId, setPreApprovalLiveChapterId] = useState("");
  const canViewChapterContact = useMemo(() =>
    canOpenNetworkMemberProfile({
      userDetails,
      isSuperAdmin,
      necMembers,
      canByKey,
    }) || isNecCouncilMember,
  [canByKey, isNecCouncilMember, isSuperAdmin, necMembers, userDetails]);
  const getVisibleContact = useCallback(
    (contact) => (canViewChapterContact ? contact || "N/A" : "-"),
    [canViewChapterContact],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behaviour: "smooth" });
  }, []);

  useEffect(
    () => () => URL.revokeObjectURL(dastakDocumentPreviewUrl),
    [dastakDocumentPreviewUrl],
  );
  const isVerifyEditRequestView =
    isViewing && isPreApprovalFlow && isEditRequestView;
  const isUnifiedReviewMode = isVerifyEditRequestView || isMemberOnlyReview;
  const [draftMemberRequests, setDraftMemberRequests] = useState([]);
  const [creatorDesignationId, setCreatorDesignationId] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // "accept" or "decline"
  const [declineReason, setDeclineReason] = useState("");
  const [declineReasonError, setDeclineReasonError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const clearRoleSelection = (userId) => {
    setUserRoleSelections((prev) => {
      const updated = { ...prev };
      delete updated[userId];
      return updated;
    });
  };

  const handleDastakDocumentChange = (event) => {
    setDastakDocument(event.target.files?.[0] || null);
    setDastakDocumentPreviewOpen(false);
  };

  const handlePreviewDastakDocument = () => {
    if (!dastakDocument) return;
    setDastakDocumentPreviewOpen(true);
  };

  const handleCloseDastakDocumentPreview = () => {
    setDastakDocumentPreviewOpen(false);
  };

  const handleEditDastakDocument = () => {
    if (!dastakDocumentInputRef.current) return;
    setDastakDocumentPreviewOpen(false);
    dastakDocumentInputRef.current.value = "";
    dastakDocumentInputRef.current.click();
  };

  const handleDeleteDastakDocument = () => {
    setDastakDocumentPreviewOpen(false);
    setDastakDocument(null);
    if (dastakDocumentInputRef.current) {
      dastakDocumentInputRef.current.value = "";
    }
  };

  const showNotification = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const closeNotification = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getChaptersListPath = () => {
    const stored = localStorage.getItem("activeDesignationSlug");
    if (stored) return `/${stored}/chapters`;

    const firstSegment = (location.pathname || "")
      .split("/")
      .filter(Boolean)[0];

    if (
      firstSegment &&
      !["chapters", "chapterDetailsPage", "chapter"].includes(firstSegment)
    ) {
      return `/${firstSegment}/chapters`;
    }

    return "/chapters";
  };

  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      chapterName: "",
      chapterUniqueCode: "",
      chapterLifecycleStatus: "",
      state: "",
      region: "",
      district: "",
      taluka: "",
      village: "",
      chapterAddress: "",
    },
  });

  const prevStateIdRef = useRef(null);
  const prevDistrictIdRef = useRef(null);
  const prevTalukaIdRef = useRef(null);

  const stateId = useWatch({ control, name: "state" });
  const regionId = useWatch({ control, name: "region" });
  const districtId = useWatch({ control, name: "district" });
  const talukaId = useWatch({ control, name: "taluka" });
  const villageId = useWatch({ control, name: "village" });

  const viewModeFieldSx = {
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: theme.palette.text.primary,
      color: theme.palette.text.primary,
      opacity: 1,
    },
    "& .MuiSelect-select.Mui-disabled": {
      WebkitTextFillColor: theme.palette.text.primary,
      color: theme.palette.text.primary,
      opacity: 1,
    },
    "& .MuiInputBase-root.Mui-disabled": {
      backgroundColor: "transparent",
    },
    "& .MuiInputLabel-root.Mui-disabled": {
      color: theme.palette.text.secondary,
    },
  };

  const villageOptions = useMemo(() => {
    const baseOptions = villages.map((v) => ({
      value: v._id,
      label: v.villageName,
    }));

    const selectedVillageValue = savedData?.village || villageId;
    if (!selectedVillageValue) return baseOptions;

    const existsInOptions = baseOptions.some(
      (opt) => String(opt.value) === String(selectedVillageValue),
    );
    if (existsInOptions) return baseOptions;

    const fallbackLabel =
      savedData?.villageName ||
      (typeof selectedVillageValue === "string" ? selectedVillageValue : "");
    if (!fallbackLabel) return baseOptions;

    return [
      ...baseOptions,
      { value: selectedVillageValue, label: fallbackLabel },
    ];
  }, [villages, savedData?.village, savedData?.villageName, villageId]);

  const handleEditChapter = () => {
    navigate(`/chapterDetailsPage/${id}/edit`, { state: { isEdit: true } });
  };

  const handleDeleteChapter = () => {
    // showNotification("Delete chapter not implemented", "info");
    setDeleteDialogOpen(true);
  };

  const confirmDeleteChapter = async () => {
    setDeleteDialogOpen(false);
    try {
      setLoading(true);
      await ApiCallService.delete(
        `${process.env.REACT_APP_BASE_URL}/chapter/${id}`,
      );
      showNotification("Chapter deleted successfully", "success");
      navigate(getChaptersListPath());
    } catch (err) {
      showNotification("Error deleting chapter", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchNecMembers = async () => {
    try {
      const res = await ApiCallService.get(
        process.env.REACT_APP_BASE_URL +
          "/committeeMembers/membersByCouncil",
        { councilName: "NEC" },
      );
      setNecMembers(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching NEC members:", err);
      setNecMembers([]);
    }
  };

  const fetchNecCouncilMembership = async () => {
    const isNecMember = await fetchIsNecCouncilMember();
    setIsNecCouncilMember(isNecMember);
  };

  useEffect(() => {
    fetchNecMembers();
    fetchNecCouncilMembership();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/users`,
      );
      setUsers(res.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
      showNotification("Error fetching users", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserByLocation = async () => {
    if (!isCreating && !isEditing) return;

    try {
      let url = "";
      if (isCreating) {
        if (districtId) {
          url = `district/users/${districtId}?limit=100`;
        } else if (talukaId) {
          url = `taluka/users/${talukaId}`;
        } else return;
      }

      if (isEditing) {
        url = `district/users/${districtId}`;
      }

      const res = await ApiCallService.get(url);
      setUsers(res.data || []);
      setUsersByLocation(res.data || []);
    } catch (err) {
      console.error("Error Found : ", err);
      setLoading(false);
    }
    setLoading(false);
  };

  const fetchPendingRequests = async (chapterIdOverride = "") => {
    const chapterIdForRequests = chapterIdOverride || id;
    if (!chapterIdForRequests) return;
    const res = await ApiCallService.get(
      `${process.env.REACT_APP_BASE_URL}/chapter-member-requests?chapter=${chapterIdForRequests}`,
    );
    const requests = Array.isArray(res)
      ? res
      : Array.isArray(res?.data)
        ? res.data
        : [];
    console.log("Fetched Pending Requests:", requests);
    setPendingRequests(requests);
  };

  useEffect(() => {
    if (hasShownPendingWarning) return;
    if (!isEditing || isPreApprovalFlow) return;
    const pending = (pendingRequests || []).filter(
      (r) => r?.status === "PENDING",
    );
    if (pending.length > 0) {
      showNotification(
        "There is already a pending request for this chapter. Submitting another edit may be rejected.",
        "warning",
      );
      setHasShownPendingWarning(true);
    }
  }, [pendingRequests, isEditing, isPreApprovalFlow, hasShownPendingWarning]);

  const fetchRoleAvailability = async () => {
    const res = await ApiCallService.get(
      `${process.env.REACT_APP_BASE_URL}/chapter-members/role-availability/${id}`,
    );
    setRoleAvailability(res.data || {});
  };

  const fetchChapterRoles = async () => {
    try {
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/chapter-members/chapterRoles`,
      );
      setChapterRoles(res.data || []);
    } catch (err) {
      console.error("Error fetching chapter roles:", err);
      showNotification("Error fetching chapter roles", "error");
    }
  };

  const fetchChapterMembers = async (overrideChapterId = "") => {
    try {
      setLoading(true);
      const effectiveLiveChapterId =
        overrideChapterId || preApprovalLiveChapterId || "";
      const membersEndpoint =
        isPreApprovalFlow && effectiveLiveChapterId
          ? `${process.env.REACT_APP_BASE_URL}/membersByChapter/${effectiveLiveChapterId}`
          : isPreApprovalFlow
            ? `${process.env.REACT_APP_BASE_URL}/chapter-requests/${id}/members`
            : `${process.env.REACT_APP_BASE_URL}/membersByChapter/${id}`;
      const res = await ApiCallService.get(membersEndpoint);
      const members = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];
      const activeMembers = members.filter((m) => m?.isActive !== false);
      setChapterMembers(activeMembers);
    } catch (err) {
      console.error("Error fetching chapter members:", err);
      showNotification("Error fetching chapter members", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/states`,
      );
      setStates(res || []);
    } catch (err) {
      console.error("Error fetching states:", err);
      showNotification("Error fetching states", "error");
    }
  };

  const fetchRegions = async (sid) => {
    try {
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/state/${sid}/region`,
      );
      setRegions(res.data || []);
    } catch (err) {
      console.error("Error fetching regions:", err);
      showNotification("Error fetching regions", "error");
    }
  };

  const fetchDistricts = async (sid) => {
    try {
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/state/${sid}/district`,
      );
      setDistricts(res.data || []);
    } catch (err) {
      console.error("Error fetching districts:", err);
      showNotification("Error fetching districts", "error");
    }
  };

  const fetchTalukas = async (did) => {
    try {
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/district/${did}/taluka`,
      );
      setTalukas(res || []);
    } catch (err) {
      console.error("Error fetching talukas:", err);
      showNotification("Error fetching talukas", "error");
    }
  };

  const fetchVillages = async (tid) => {
    try {
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/taluka/${tid}/village`,
      );
      const villageList = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setVillages(villageList);
    } catch (err) {
      console.error("Error fetching villages:", err);
      showNotification("Error fetching villages", "error");
    }
  };

  const isRoleBlocked = (roleCode) => {
    return (
      chapterMembers.some((m) => m.role.code === roleCode) ||
      pendingRequests.some(
        (r) =>
          r.requestedRole?.code === roleCode &&
          r.status === "PENDING" &&
          r.action !== "REQUESTED_REMOVE",
      )
    );
  };

  const hasPendingForMember = (memberId) => {
    return pendingRequests.some(
      (r) =>
        r.user?._id === memberId &&
        r.status === "PENDING" &&
        r.action !== "REQUESTED_REMOVE",
    );
  };

  const hasPendingForUser = (userId) => {
    return pendingRequests.some(
      (r) =>
        r.user?._id === userId &&
        r.status === "PENDING" &&
        r.action !== "REQUESTED_REMOVE",
    );
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);

        // All modes need states
        await fetchStates();

        if (isCreating) {
          // CREATE mode: only need location dropdowns

          await fetchChapterRoles();
        } else if (isViewing) {
          // VIEW mode: fetch chapter data + members + pending requests
          await fetchChapterRoles();
          await fetchChapterMembers();
          if (!isPreApprovalFlow) {
            await fetchPendingRequests();
            await fetchRoleAvailability();
          }
        } else if (isEditing) {
          // EDIT mode: fetch chapter data + members + users + pending requests
          // await fetchUsers();
          await fetchUserByLocation();
          await fetchChapterRoles();
          await fetchChapterMembers();
          await fetchPendingRequests();
        }
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [id, mode, isPreApprovalFlow]);

  useEffect(() => {
    if (!stateId) return;

    if (prevStateIdRef.current && prevStateIdRef.current !== stateId) {
      setValue("region", "");
      setValue("district", "");
      setValue("taluka", "");
      setValue("village", "");
    }

    prevStateIdRef.current = stateId;
    fetchRegions(stateId);
    fetchDistricts(stateId);
  }, [stateId]);

  useEffect(() => {
    if (!districtId) return;

    if (prevDistrictIdRef.current && prevDistrictIdRef.current !== districtId) {
      setValue("taluka", "");
      setValue("village", "");
    }

    prevDistrictIdRef.current = districtId;
    fetchTalukas(districtId);
  }, [districtId]);

  useEffect(() => {
    if (talukaId) {
      if (prevTalukaIdRef.current && prevTalukaIdRef.current !== talukaId) {
        setValue("village", "");
      }
      prevTalukaIdRef.current = talukaId;
      fetchVillages(talukaId);
    } else {
      setVillages([]);
    }
  }, [talukaId]);

  useEffect(() => {
    if (isCreating) {
      fetchUserByLocation();
    }
  }, [stateId, districtId, talukaId]);

  useEffect(() => {
    const stateName =
      states.find((s) => String(s._id) === String(stateId))?.stateName || "";

    const regionName =
      regions.find((r) => String(r._id) === String(regionId))?.regionName || "";

    const districtName =
      districts.find((d) => String(d._id) === String(districtId))
        ?.districtName || "";

    const talukaName =
      talukas.find((t) => String(t._id) === String(talukaId))?.talukaName || "";

    const villageName =
      villages.find((v) => String(v._id) === String(villageId))?.villageName ||
      "";

    const address = [
      villageName,
      talukaName,
      districtName,
      regionName,
      stateName,
    ]
      .filter(Boolean)
      .join(", ");

    if (address) {
      setValue("chapterAddress", address); // <-- Only update the address field
    }
  }, [
    stateId,
    regionId,
    districtId,
    talukaId,
    villageId,
    states,
    regions,
    districts,
    talukas,
    villages,
  ]);

  useEffect(() => {
    if ((isViewing || isEditing) && !id) return;

    const fetchChapter = async () => {
      try {
        const chapterEndpoint = isPreApprovalFlow
          ? `${process.env.REACT_APP_BASE_URL}/chapter-requests/${id}`
          : `${process.env.REACT_APP_BASE_URL}/getChapterById/${id}`;
        const res = await ApiCallService.get(chapterEndpoint);
        const chapter = res?.data || res || null;

        if (!chapter) {
          showNotification("Chapter details not found", "error");
          return;
        }

        const chapterLocation = chapter.location || {};
        const stateValue =
          chapterLocation?.state?._id || chapterLocation?.state || "";
        const regionValue =
          chapterLocation?.region?._id || chapterLocation?.region || "";
        const districtValue =
          chapterLocation?.district?._id || chapterLocation?.district || "";
        const talukaValue =
          chapterLocation?.taluka?._id || chapterLocation?.taluka || "";
        const villageValue =
          chapterLocation?.village?._id || chapterLocation?.village || "";

        const getLabelById = (list, id, labelKey) =>
          (Array.isArray(list) ? list : []).find(
            (item) => String(item?._id) === String(id),
          )?.[labelKey] || "";

        const chapterAddress = [
          chapterLocation?.village?.villageName ||
            getLabelById(villages, villageValue, "villageName"),
          chapterLocation?.taluka?.talukaName ||
            getLabelById(talukas, talukaValue, "talukaName"),
          chapterLocation?.district?.districtName ||
            getLabelById(districts, districtValue, "districtName"),
          chapterLocation?.state?.stateName ||
            getLabelById(states, stateValue, "stateName"),
        ]
          .filter(Boolean)
          .join(", ");

        const districtNameFromLocation =
          chapterLocation?.district?.districtName || "";
        const talukaNameFromLocation =
          chapterLocation?.taluka?.talukaName || "";
        const villageNameFromLocation =
          chapterLocation?.village?.villageName || "";
        const regionNameFromLocation =
          chapterLocation?.region?.regionName || "";

        const payload = {
          chapterName: chapter.chapterName || chapter.proposedChapterName || "",
          chapterUniqueCode: chapter.chapterUniqueCode || "",
          chapterLifecycleStatus: chapter.chapterLifecycleStatus || "",
          state: stateValue,
          region: regionValue,
          district: districtValue,
          taluka: talukaValue,
          village: villageValue,
          chapterAddress: chapterAddress || "",
        };

        setSavedData({
          ...payload,
          districtName: districtNameFromLocation,
          talukaName: talukaNameFromLocation,
          villageName: villageNameFromLocation,
          regionName: regionNameFromLocation,
        });
        reset(payload);

        if (talukaValue) {
          await fetchVillages(talukaValue);
          if (villageValue) {
            setValue("village", villageValue);
          }
        }

        if (
          regionValue &&
          regionNameFromLocation &&
          !regions.some((r) => String(r._id) === String(regionValue))
        ) {
          setRegions((prev) => [
            ...((Array.isArray(prev) ? prev : []) || []),
            { _id: regionValue, regionName: regionNameFromLocation },
          ]);
        }

        if (
          districtValue &&
          districtNameFromLocation &&
          !districts.some((d) => String(d._id) === String(districtValue))
        ) {
          setDistricts((prev) => [
            ...((Array.isArray(prev) ? prev : []) || []),
            { _id: districtValue, districtName: districtNameFromLocation },
          ]);
        }

        if (
          talukaValue &&
          talukaNameFromLocation &&
          !talukas.some((t) => String(t._id) === String(talukaValue))
        ) {
          setTalukas((prev) => [
            ...((Array.isArray(prev) ? prev : []) || []),
            { _id: talukaValue, talukaName: talukaNameFromLocation },
          ]);
        }

        if (
          villageValue &&
          villageNameFromLocation &&
          !villages.some((v) => String(v._id) === String(villageValue))
        ) {
          setVillages((prev) => [
            ...((Array.isArray(prev) ? prev : []) || []),
            { _id: villageValue, villageName: villageNameFromLocation },
          ]);
        }
        if (isPreApprovalFlow && chapter?.liveChapter) {
          setIsEditRequestView(true);
          const liveChapterId =
            typeof chapter.liveChapter === "object"
              ? chapter.liveChapter?._id
              : chapter.liveChapter;
          setPreApprovalLiveChapterId(liveChapterId || "");

          if (liveChapterId) {
            const liveRes = await ApiCallService.get(
              `${process.env.REACT_APP_BASE_URL}/getChapterById/${liveChapterId}`,
            );
            const live = liveRes?.data || liveRes || null;
            await fetchChapterMembers(liveChapterId);
            await fetchPendingRequests(liveChapterId);

            if (live) {
              const liveLocation = live.location || {};

              const resolveLocationDisplay = (value, list, labelKey) => {
                if (!value) return "-";
                if (typeof value === "object") {
                  return value?.[labelKey] || value?.name || "-";
                }
                const found = (Array.isArray(list) ? list : []).find(
                  (x) => String(x?._id) === String(value),
                );
                if (found?.[labelKey]) return found[labelKey];
                const looksLikeObjectId =
                  typeof value === "string" && /^[a-f0-9]{24}$/i.test(value);
                return looksLikeObjectId ? "-" : String(value);
              };

              const rows = [
                {
                  label: "Chapter Name",
                  previous: live.chapterName || "-",
                  current: chapter.chapterName || "-",
                  group: "Chapter Information",
                },
                {
                  label: "Chapter Unique Code",
                  previous: live.chapterUniqueCode || "-",
                  current: chapter.chapterUniqueCode || "-",
                  group: "Chapter Information",
                },
                {
                  label: "State",
                  previous: resolveLocationDisplay(
                    liveLocation?.state,
                    states,
                    "stateName",
                  ),
                  current: resolveLocationDisplay(
                    chapterLocation?.state || stateValue,
                    states,
                    "stateName",
                  ),
                  group: "Location Information",
                },
                {
                  label: "Region",
                  previous: resolveLocationDisplay(
                    liveLocation?.region,
                    regions,
                    "regionName",
                  ),
                  current: resolveLocationDisplay(
                    chapterLocation?.region || regionValue,
                    regions,
                    "regionName",
                  ),
                  group: "Location Information",
                },
                {
                  label: "District",
                  previous: resolveLocationDisplay(
                    liveLocation?.district,
                    districts,
                    "districtName",
                  ),
                  current: resolveLocationDisplay(
                    chapterLocation?.district || districtValue,
                    districts,
                    "districtName",
                  ),
                  group: "Location Information",
                },
                {
                  label: "Taluka",
                  previous: resolveLocationDisplay(
                    liveLocation?.taluka,
                    talukas,
                    "talukaName",
                  ),
                  current: resolveLocationDisplay(
                    chapterLocation?.taluka || talukaValue,
                    talukas,
                    "talukaName",
                  ),
                  group: "Location Information",
                },
                {
                  label: "Village",
                  previous: resolveLocationDisplay(
                    liveLocation?.village,
                    villages,
                    "villageName",
                  ),
                  current: resolveLocationDisplay(
                    chapterLocation?.village || villageValue,
                    villages,
                    "villageName",
                  ),
                  group: "Location Information",
                },
                {
                  label: "Lifecycle Status",
                  previous: live.chapterLifecycleStatus || "-",
                  current: chapter.chapterLifecycleStatus || "-",
                  group: "Chapter Information",
                },
              ].map((r) => ({
                ...r,
                changed: String(r.previous || "") !== String(r.current || ""),
              }));

              setChapterEditDiffs(rows);
            }
          }
        } else {
          setIsEditRequestView(false);
          setChapterEditDiffs([]);
          setPreApprovalLiveChapterId("");
        }
      } catch (err) {
        console.error("Error fetching chapter:", err);
        showNotification("Error fetching chapter details", "error");
      }
    };

  //   const handleChapterCreate = async () => {
  //     try {
  //       setLoading(true);
  //       const res = await ApiCallService.post(
  //         `${process.env.REACT_APP_BASE_URL}/registerChapter`,
  //       );
  //     } catch (err) {
  //       setLoading(false);
  //       console.error("Error Found", err);
  //     } finally {
  //       setLoading(true);
  //     }
  //   };

    if ((isViewing || isEditing) && id) {
      fetchChapter();
    }
  }, [id, isViewing, isEditing, isPreApprovalFlow]);

  useEffect(() => {
    if (isViewing && talukas.length > 0 && savedData?.taluka) {
      setValue("taluka", savedData.taluka);
    }
  }, [isViewing, talukas, savedData?.taluka, setValue]);

  useEffect(() => {
    if (villages.length > 0 && savedData?.village) {
      setValue("village", savedData.village);
    }
  }, [villages]);

  const handleBack = () => {
    navigate(-1);
  };

  const mergedChapterRows = useMemo(() => {
    if (isPreApprovalFlow && !isEditRequestView) {
      return (chapterMembers || []).map((m) => ({
        id: m?._id || `${m?.user?._id || m?.user}-${m?.role?._id || m?.role}`,
        userId: m?.user?._id || m?.user || "",
        name:
          m?.user?.userFirstName || m?.user?.userLastName
            ? `${m?.user?.userFirstName || ""} ${m?.user?.userLastName || ""}`.trim()
            : "N/A",
        mobile: m?.user?.mobileNumber || "N/A",
        role: m?.role?.name || "N/A",
        roleId: m?.role?._id || m?.role || "",
        status: isEditRequestView ? "APPROVED" : "PENDING",
      }));
    }

    const map = new Map();

    chapterMembers.forEach((m) => {
      map.set(m.user._id, {
        id: m._id,
        userId: m.user._id,
        name: `${m.user.userFirstName} ${m.user.userLastName}`,
        mobile: m.user.mobileNumber,
        role: m.role.name,
        roleId: m.role._id,
        status: "APPROVED",
      });
    });

    pendingRequests
      .filter((r) => r.status === "PENDING")
      .forEach((r) => {
        const userId = r.user._id;

        const row = {
          id: r._id,
          userId,
          name: `${r.user.userFirstName} ${r.user.userLastName}`,
          mobile: r.user.mobileNumber,
          role:
            r.action === "REQUESTED_REMOVE"
              ? r.currentRole?.name || "Unassign"
              : r.requestedRole?.name || "Unassign",
          roleId:
            r.action === "REQUESTED_REMOVE"
              ? r.currentRole?._id || ""
              : r.requestedRole?._id || "",
          status: "PENDING",
          actionType: r.action,
        };

        if (r.action === "REQUESTED_ADD") {
          map.set(userId, row);
        } else if (r.action === "REQUESTED_ROLE_CHANGE") {
          map.set(userId, row);
        } else if (r.action === "REQUESTED_REMOVE") {
          map.set(userId, row);
        }
      });

    const merged = Array.from(map.values());

    if (!isEditing || draftMemberRequests.length === 0) {
      return merged;
    }

    const draftMap = new Map(merged.map((r) => [r.userId, r]));
    draftMemberRequests.forEach((req) => {
      if (req.actionType === "REQUESTED_ROLE_CHANGE") {
        const existing = draftMap.get(req.userId);
        const role = chapterRoles.find((r) => r._id === req.requestedRoleId);
        if (existing) {
          draftMap.set(req.userId, {
            ...existing,
            role: role?.name || existing.role,
            roleId: req.requestedRoleId,
            status: "DRAFT",
            actionType: "REQUESTED_ROLE_CHANGE_DRAFT",
          });
        }
      }

      if (req.actionType === "REQUESTED_ADD") {
        const role = chapterRoles.find((r) => r._id === req.requestedRoleId);
        const user = users.find((u) => u._id === req.userId);
        if (!draftMap.has(req.userId) && user) {
          draftMap.set(req.userId, {
            id: `draft-add-${req.userId}`,
            userId: req.userId,
            name: `${user.userFirstName || ""} ${user.userLastName || ""}`.trim(),
            mobile: user.mobileNumber || "N/A",
            role: role?.name || "N/A",
            roleId: req.requestedRoleId,
            status: "DRAFT",
            actionType: "REQUESTED_ADD_DRAFT",
          });
        }
      }

      if (req.actionType === "REQUESTED_REMOVE") {
        const existing = draftMap.get(req.userId);
        if (existing) {
          draftMap.set(req.userId, {
            ...existing,
            status: "DRAFT",
            actionType: "REQUESTED_REMOVE_DRAFT",
          });
        }
      }
    });

    return Array.from(draftMap.values());
  }, [
    chapterMembers,
    pendingRequests,
    isPreApprovalFlow,
    isEditRequestView,
    isEditing,
    draftMemberRequests,
    chapterRoles,
    users,
  ]);

  const validateRoleAssignment = (roleId, userId, excludeMemberId = null) => {
    const role = chapterRoles.find((r) => r._id === roleId);
    if (!role) return true;

    const isSingleSeatRole =
      role?.code === "CHAPTER_PRESIDENT" || role?.code === "CHAPTER_SECRETARY";

    // Check if this specific role is already assigned to an approved member
    const activeRoleHolder = chapterMembers?.find(
      (m) =>
        m.role?._id === roleId &&
        m._id !== excludeMemberId &&
        m.user?._id !== userId,
    );

    const roleAlreadyAssigned = !!activeRoleHolder;

    const roleAlreadyInLocal = chapterMembersLocal.some(
      (m) => m.roleId === roleId,
    );

    const roleAlreadyInDraft = draftMemberRequests.some(
      (r) =>
        r.actionType !== "REQUESTED_REMOVE" &&
        r.requestedRoleId === roleId &&
        r.userId !== userId,
    );

    if (isSingleSeatRole && roleAlreadyAssigned) {
      const holderPendingRelease = pendingRequests?.some(
        (r) =>
          r.status === "PENDING" &&
          r.user?._id === activeRoleHolder?.user?._id &&
          (r.action === "REQUESTED_REMOVE" ||
            (r.action === "REQUESTED_ROLE_CHANGE" &&
              r.requestedRole?._id !== roleId)),
      );
      const holderDraftRelease = draftMemberRequests.some(
        (r) =>
          r.userId === activeRoleHolder?.user?._id &&
          r.actionType === "REQUESTED_ROLE_CHANGE" &&
          r.requestedRoleId !== roleId,
      );

      if (holderPendingRelease || holderDraftRelease) {
        showNotification(`${role.name} downgrade/removal is pending.`, "error");
        clearRoleSelection(userId);
        return false;
      }

      showNotification(`Only one ${role.name} allowed per chapter`, "error");
      clearRoleSelection(userId);
      return false;
    }

    if (isSingleSeatRole && (roleAlreadyInLocal || roleAlreadyInDraft)) {
      showNotification(`Only one ${role.name} allowed`, "error");
      return false;
    }

    // Check if this specific role has a pending request (excluding REMOVE actions)
    if (isSingleSeatRole) {
      const rolePending = pendingRequests?.some(
        (r) =>
          r.requestedRole?._id === roleId &&
          r.status === "PENDING" &&
          r.action !== "REQUESTED_REMOVE",
      );

      if (rolePending) {
        showNotification(
          `A ${role.name} role change is already pending approval`,
          "error",
        );
        clearRoleSelection(userId);
        return false;
      }
    }

    return true;
  };

  const handleUpdateMemberRole = async (memberId, newRoleId) => {
    if (!newRoleId) {
      showNotification("Please select a role", "error");
      return;
    }

    const member = chapterMembers.find((m) => m._id === memberId);
    if (!member) {
      showNotification("Member not found", "error");
      return;
    }
    if (String(member.role?._id) === String(newRoleId)) {
      return;
    }

    if (!validateRoleAssignment(newRoleId, member.user?._id, memberId)) return;

    setDraftMemberRequests((prev) => {
      const remaining = prev.filter(
        (r) =>
          !(
            r.userId === member.user?._id &&
            (r.actionType === "REQUESTED_ROLE_CHANGE" ||
              r.actionType === "REQUESTED_REMOVE")
          ),
      );
      return [
        ...remaining,
        {
          chapterId: id,
          userId: member.user?._id,
          actionType: "REQUESTED_ROLE_CHANGE",
          currentRoleId: member.role?._id,
          requestedRoleId: newRoleId,
        },
      ];
    });
    showNotification(
      "Role change queued. Click Update Chapter to submit.",
      "success",
    );
  };

  const queueMemberAddDraft = (userId, roleId) => {
    if (!roleId) return;
    if (!validateRoleAssignment(roleId, userId)) return;

    setDraftMemberRequests((prev) => {
      const filtered = prev.filter(
        (r) => !(r.userId === userId && r.actionType === "REQUESTED_ADD"),
      );
      return [
        ...filtered,
        {
          chapterId: id,
          userId,
          actionType: "REQUESTED_ADD",
          requestedRoleId: roleId,
        },
      ];
    });
    showNotification(
      "Member add queued. Click Update Chapter to submit.",
      "success",
    );
  };

  const queueMemberRemoveDraft = (row) => {
    const member = row?.fullMember;
    if (!member?.user?._id) return;
    const userId = member.user._id;

    if (hasPendingForUser(userId)) {
      showNotification("This member already has a pending request.", "error");
      return;
    }

    setDraftMemberRequests((prev) => {
      const filtered = prev.filter((r) => r.userId !== userId);
      return [
        ...filtered,
        {
          chapterId: id,
          userId,
          actionType: "REQUESTED_REMOVE",
          currentRoleId: member.role?._id,
        },
      ];
    });
    showNotification(
      "Member removal queued. Click Update Chapter to submit.",
      "success",
    );
  };

  const undoDraftMemberChange = (row) => {
    if (!row?.userId) return;
    setPendingAddRoles((prev) => {
      const updated = { ...prev };
      delete updated[row.userId];
      return updated;
    });
    clearRoleSelection(row.userId);
    setDraftMemberRequests((prev) =>
      prev.filter((requestItem) => {
        if (requestItem.userId !== row.userId) return true;
        if (row.actionType === "REQUESTED_ADD_DRAFT") {
          return requestItem.actionType !== "REQUESTED_ADD";
        }
        if (row.actionType === "REQUESTED_REMOVE_DRAFT") {
          return requestItem.actionType !== "REQUESTED_REMOVE";
        }
        if (row.actionType === "REQUESTED_ROLE_CHANGE_DRAFT") {
          return requestItem.actionType !== "REQUESTED_ROLE_CHANGE";
        }
        return true;
      }),
    );
    showNotification("Draft member change undone", "info");
  };

  const handleChapterVerification = async (chapterApproval, reason = "") => {
    if (!id || !isPreApprovalFlow) return;

    try {
      setLoading(true);
      const verifyEndpoint = isEditRequestView
        ? `${process.env.REACT_APP_BASE_URL}/chapter/verify-with-members/${id}`
        : `${process.env.REACT_APP_BASE_URL}/chapter/verify/${id}`;
      await ApiCallService.put(verifyEndpoint, {
        chapterApproval,
        chapterApprovalHistory: { reason: chapterApproval ? "" : reason },
        storedUserId: localStorage.getItem("userId"),
      });
      showNotification(
        chapterApproval
          ? "Chapter request accepted successfully"
          : "Chapter request declined successfully",
        "success",
      );
      navigate("/chaptersVerification");
    } catch (err) {
      console.error("Error verifying chapter request:", err);
      showNotification("Failed to process chapter verification", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleMemberOnlyVerification = async (approve, reason = "") => {
    try {
      setLoading(true);
      const pending = (pendingRequests || []).filter(
        (r) => r?.status === "PENDING",
      );
      if (pending.length === 0) {
        showNotification("No pending member requests found", "info");
        return;
      }

      const endpointFor = (requestId) =>
        approve
          ? `${process.env.REACT_APP_BASE_URL}/chapter-member-requests/${requestId}/approve`
          : `${process.env.REACT_APP_BASE_URL}/chapter-member-requests/${requestId}/reject`;

      const settled = await Promise.allSettled(
        pending.map((r) =>
          ApiCallService.post(endpointFor(r?._id), {
            reason: approve ? "" : reason,
          }),
        ),
      );

      const failed = settled.filter((s) => s.status === "rejected").length;
      if (failed > 0) {
        showNotification(
          `${pending.length - failed} processed, ${failed} failed. Please retry.`,
          "warning",
        );
      } else {
        showNotification(
          approve
            ? "Member change requests accepted successfully"
            : "Member change requests declined successfully",
          "success",
        );
      }
      navigate("/chaptersVerification");
    } catch (err) {
      console.error("Error verifying member-only review:", err);
      showNotification("Failed to process member requests", "error");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    const requiredCreateFields = [
      { key: "chapterName", label: "Chapter Name" },
      { key: "chapterLifecycleStatus", label: "Chapter Lifecycle Status" },
      { key: "state", label: "State" },
      { key: "region", label: "Region" },
      { key: "district", label: "District" },
    ];

    if (isCreating) {
      const missingField = requiredCreateFields.find(({ key }) => {
        const value = data?.[key];
        return (
          value === null || value === undefined || String(value).trim() === ""
        );
      });
      if (missingField) {
        showNotification(`Please select/enter ${missingField.label}`, "error");
        return;
      }
    }

    const currentUserId =
      userDetails?._id ||
      userDetails?.id ||
      localStorage.getItem("userId") ||
      "";

    if (isDastakChapter && isCreating && !creatorDesignationId) {
      showNotification(
        "Please select your designation for this Dastak chapter",
        "error",
      );
      return;
    }

    if (isDastakChapter && isCreating && !dastakDocument) {
      showNotification(
        "Please upload a document for this Dastak chapter",
        "error",
      );
      return;
    }

    const requestedMembers = chapterMembersLocal.map((member) => ({
      userId: member.userId,
      roleId: member.roleId,
    }));

    if (
      isDastakChapter &&
      isCreating &&
      creatorDesignationId &&
      currentUserId &&
      !requestedMembers.some(
        (member) => String(member.userId) === String(currentUserId),
      )
    ) {
      requestedMembers.push({
        userId: currentUserId,
        roleId: creatorDesignationId,
      });
    }

    const payload = {
      chapterName: data.chapterName,
      chapterUniqueCode: isEditing
        ? savedData?.chapterUniqueCode || data.chapterUniqueCode
        : data.chapterUniqueCode,
      chapterLifecycleStatus: data.chapterLifecycleStatus,
      // chapterType: data.chapterType,  no field to get this value but do we need to take from user or what?
      // chapterStatus: data.chapterStatus,

      location: {
        state: data.state,
        region: data.region,
        district: data.district,
        taluka: data.taluka,
        village: data.village,
      },

      excelMeta: {
        excelId: "",
        existingOrNew: "new",
        dataVerfication: false,
        locationDataMissing: false,
      },

      // village: data.village,
      // chapterAddress: data.chapterAddress,

      createdBy: "",
      chapterLifecycleStatus: data.chapterLifecycleStatus,
    };

    if (requestedMembers.length > 0) {
      payload.members = requestedMembers.map((member) => ({
        user: member.userId,
        role: member.roleId,
        action: "REQUESTED_ADD",
        status: "PENDING",
        requestedAt: new Date().toISOString(),
      }));
    }

    if (isDastakChapter) {
      payload.isSadhuSadhviChapter = true;
    }

    console.log("====== FINAL PAYLOAD ======");
    console.log(payload);

    try {
      setLoading(true);

      if (isCreating) {
        let res;

        if (isDastakChapter) {
          const formData = new FormData();
          formData.append("file", dastakDocument);

          const uploadEndpoint = dastakDocument.type?.startsWith("image/")
            ? `${process.env.REACT_APP_BASE_URL}/upload/document/image`
            : `${process.env.REACT_APP_BASE_URL}/upload/document`;

          let uploadRes;
          try {
            uploadRes = await ApiCallService.post(uploadEndpoint, formData, {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: 60000,
            });
          } catch (uploadError) {
            if (
              uploadError?.code === "ECONNABORTED" ||
              uploadError?.code === "ETIMEDOUT"
            ) {
              throw new Error(
                "Document upload timed out. Please try again.",
              );
            }
            throw uploadError;
          }

          const uploadedDocumentUrl =
            uploadRes?.document ||
            uploadRes?.data?.document ||
            uploadRes?.data?.data?.document ||
            uploadRes?.url ||
            uploadRes?.data?.url ||
            uploadRes?.key ||
            uploadRes?.data?.key;

          if (!uploadedDocumentUrl) {
            throw new Error("Document upload returned no URL");
          }

          payload.sadhuSadhviDocumentUrl = uploadedDocumentUrl;

          res = await ApiCallService.post(
            `${process.env.REACT_APP_BASE_URL}/createNewDastakChapter`,
            payload,
          );
        } else {
          res = await ApiCallService.post(
            `${process.env.REACT_APP_BASE_URL}/chapter`,
            payload,
          );
        }

        showNotification(
          res?.message ||
            (canApplyChapterChanges
              ? "Chapter created successfully"
              : "Chapter request submitted for approval"),
          "success",
        );
        if (isCreating && isDastakChapter) {
          navigate("/dastakForm");
        } else {
          navigate(getChaptersListPath());
        }
        // navigate(`/chapterDetailsPage/${res.data._id}`);
      } else if (isEditing) {
        const normalize = (v) => String(v ?? "").trim();
        const hasChapterFieldChanges = savedData
          ? normalize(data.chapterName) !== normalize(savedData.chapterName) ||
            normalize(data.chapterLifecycleStatus) !==
              normalize(savedData.chapterLifecycleStatus) ||
            normalize(data.state) !== normalize(savedData.state) ||
            normalize(data.region) !== normalize(savedData.region) ||
            normalize(data.district) !== normalize(savedData.district) ||
            normalize(data.taluka) !== normalize(savedData.taluka) ||
            normalize(data.village) !== normalize(savedData.village)
          : true;

        if (!hasChapterFieldChanges && draftMemberRequests.length === 0) {
          showNotification("No changes to submit", "info");
          return;
        }

        let editRes = null;

        const editRequestPayload = {
          liveChapterId: id,
          chapterName: data.chapterName,
          chapterUniqueCode: payload.chapterUniqueCode,
          chapterLifecycleStatus: payload.chapterLifecycleStatus,
          location: payload.location,
          excelMeta: {
            ...payload.excelMeta,
            existingOrNew: "edit",
          },
        };

        if (hasChapterFieldChanges) {
          const editRes = await ApiCallService.post(
            `${process.env.REACT_APP_BASE_URL}/chapter-requests/edit`,
            editRequestPayload,
          );
        }

        let failedMemberRequests = 0;
        if (draftMemberRequests.length > 0) {
          const settled = await Promise.allSettled(
            draftMemberRequests.map((requestItem) =>
              ApiCallService.post(
                `${process.env.REACT_APP_BASE_URL}/chapter-member-requests`,
                {
                  chapterId: id,
                  userId: requestItem.userId,
                  actionType: requestItem.actionType,
                  currentRoleId: requestItem.currentRoleId,
                  requestedRoleId: requestItem.requestedRoleId,
                },
              ),
            ),
          );
          failedMemberRequests = settled.filter(
            (item) => item.status === "rejected",
          ).length;
        }

        if (failedMemberRequests > 0) {
          showNotification(
            `Chapter edit request submitted, but ${failedMemberRequests} member request(s) failed. Please retry.`,
            "warning",
          );
        } else {
          const successMessage = canApplyChapterChanges
            ? hasChapterFieldChanges && draftMemberRequests.length > 0
              ? "Chapter and member changes applied successfully"
              : hasChapterFieldChanges
                ? editRes?.message || "Chapter updated successfully"
                : "Member changes applied successfully"
            : hasChapterFieldChanges && draftMemberRequests.length > 0
              ? "Chapter and member change requests submitted for approval"
              : hasChapterFieldChanges
                ? "Chapter edit request submitted for approval"
                : "Member change requests submitted for approval";
          showNotification(successMessage, "success");
          setDraftMemberRequests([]);
        }
        navigate(`/chapterDetailsPage/${id}`, { replace: true });
      }
    } catch (err) {
      console.error("Error submitting chapter:", err);
      const apiMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Error submitting chapter";
      showNotification(apiMessage, "error");
    } finally {
      setLoading(false);
    }
  };
  const colsInChapterTable = [
    { field: "memberName", headerName: "Member Name" },
    ...(canViewChapterContact
      ? [{ field: "contact", headerName: "Contact" }]
      : []),
    {
      field: "status",
      headerName: "Status",
      renderCell: (row) => (
        <Chip
          label={row.status}
          color={
            row.status === "PENDING"
              ? "warning"
              : row.status === "DRAFT"
                ? "info"
                : "success"
          }
          size="small"
        />
      ),
    },
    {
      field: "role",
      headerName: "Role",
      renderCell: (row) => {
        if (!isEditing) return <span>{row.designation}</span>;

        const memberId = row.fullMember?._id || row.id;
        const value = row.fullMember?.role?._id ?? row.roleId ?? "";
        const isAddDraft = row.actionType === "REQUESTED_ADD_DRAFT";
        const isPending =
          hasPendingForUser(row.userId) || row.status === "PENDING";

        return (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={value}
              disabled={isPending || !isEditing || isAddDraft}
              onChange={(e) => handleUpdateMemberRole(memberId, e.target.value)}
            >
              {chapterRoles.map((r) => (
                <MenuItem key={r._id} value={r._id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
    // Only include the action column if not viewing
    ...(!isViewing
      ? [
          {
            field: "action",
            headerName: "Action",
            renderCell: (row) => {
              const isPending =
                hasPendingForUser(row.userId) || row.status === "PENDING";
              return (
                <>
                  {isEditing && row.status === "APPROVED" ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={isPending}
                      onClick={() => queueMemberRemoveDraft(row)}
                    >
                      Remove
                    </Button>
                  ) : isEditing && row.status === "DRAFT" ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => undoDraftMemberChange(row)}
                    >
                      Undo
                    </Button>
                  ) : isCreating ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleRemoveMember(row.userId)}
                    >
                      Undo
                    </Button>
                  ) : null}
                </>
              );
            },
          },
        ]
      : []),
  ];

  // console.log("Merged Chapter Rows:", mergedChapterRows);

  const rowsInChapterTable = isCreating
    ? chapterMembersLocal.map((member) => ({
        ...member,
        contact: getVisibleContact(member.contact),
      }))
    : mergedChapterRows.map((r) => ({
        memberName: r.name,
        contact: getVisibleContact(r.mobile),
        designation: r.role,
        userId: r.userId,
        fullMember:
          r.status === "APPROVED"
            ? chapterMembers.find((m) => m._id === r.id)
            : null,
        id: r.id,
        roleId: r.roleId,
        status: r.status,
        actionType: r.actionType,
      }));

  const memberChangeDiffs = useMemo(() => {
    if (!isUnifiedReviewMode) return [];

    return (pendingRequests || [])
      .filter((r) => r?.status === "PENDING")
      .map((r) => {
        const action = r?.action || "";
        const memberName =
          `${r?.user?.userFirstName || ""} ${r?.user?.userLastName || ""}`.trim() ||
          "N/A";
        const currentRoleName = r?.currentRole?.name || "-";
        const requestedRoleName = r?.requestedRole?.name || "-";

        if (action === "REQUESTED_ADD") {
          return {
            id: r?._id || `${r?.user?._id}-add`,
            memberName,
            mobile: r?.user?.mobileNumber || "N/A",
            action,
            previous: "-",
            current: requestedRoleName,
            requestedAt: r?.requestedAt,
          };
        }

        if (action === "REQUESTED_REMOVE") {
          return {
            id: r?._id || `${r?.user?._id}-remove`,
            memberName,
            mobile: r?.user?.mobileNumber || "N/A",
            action,
            previous: currentRoleName,
            current: "-",
            requestedAt: r?.requestedAt,
          };
        }

        return {
          id: r?._id || `${r?.user?._id}-role-change`,
          memberName,
          mobile: r?.user?.mobileNumber || "N/A",
          action,
          previous: currentRoleName,
          current: requestedRoleName,
          requestedAt: r?.requestedAt,
        };
      });
  }, [isUnifiedReviewMode, pendingRequests]);

  const shouldShowChapterMembersTable = isCreating
    ? rowsInChapterTable.length > 0
    : rowsInChapterTable.length > 0;

  // Available users for adding (excluding already added members and users with pending requests)
  const availableUsers = useMemo(() => {
    const memberIds = new Set(chapterMembers.map((m) => m.user._id));
    const localAddedIds = new Set(chapterMembersLocal.map((m) => m.userId));
    const draftAddedIds = new Set(
      draftMemberRequests
        .filter((r) => r.actionType === "REQUESTED_ADD")
        .map((r) => r.userId),
    );

    const pendingAddIds = new Set(
      pendingRequests
        .filter((r) => r.status === "PENDING" && r.action === "REQUESTED_ADD")
        .map((r) => r.user._id),
    );
    return users.filter(
      (u) =>
        !memberIds.has(u._id) &&
        !pendingAddIds.has(u._id) &&
        !localAddedIds.has(u._id) &&
        !draftAddedIds.has(u._id),
    );
  }, [
    users,
    chapterMembers,
    chapterMembersLocal,
    pendingRequests,
    draftMemberRequests,
  ]);

  const handleAddMemberFromUserTable = (row) => {
    const selectedRoleId =
      userRoleSelections[row.userId] || pendingAddRoles[row.userId];
    if (!selectedRoleId) {
      showNotification("Please select a role", "error");
      return;
    }
    if (!validateRoleAssignment(selectedRoleId, row.userId)) return;

    if (isCreating) {
      const role = chapterRoles.find((r) => r._id === selectedRoleId);
      const alreadyAdded = chapterMembersLocal.some(
        (m) => m.userId === row.userId,
      );
      if (alreadyAdded) return;

      setChapterMembersLocal((prev) => [
        ...prev,
        {
          id: row.userId,
          userId: row.userId,
          roleId: selectedRoleId,
          memberName: row.userFullName,
          contact: row.rawMobileNumber || row.mobileNumber,
          designation: role?.name || "",
          status: "PENDING",
        },
      ]);
      setUsers((prev) => prev.filter((u) => u._id !== row.userId));
      showNotification("Member added as pending", "success");
      return;
    }

    queueMemberAddDraft(row.userId, selectedRoleId);
    setPendingAddRoles((prev) => ({ ...prev, [row.userId]: selectedRoleId }));
  };

  const handleRemoveMemberFromUserTable = (row) => {
    if (isCreating) {
      setChapterMembersLocal((prev) =>
        prev.filter((m) => m.userId !== row.userId),
      );
      const originalUser = usersByLocation.find((u) => u._id === row.userId);
      if (originalUser) {
        setUsers((prev) => {
          const exists = prev.some((u) => u._id === originalUser._id);
          return exists ? prev : [...prev, originalUser];
        });
      }
      setPendingAddRoles((prev) => {
        const updated = { ...prev };
        delete updated[row.userId];
        return updated;
      });
      clearRoleSelection(row.userId);
      showNotification("Member removed", "info");
      return;
    }

    setDraftMemberRequests((prev) =>
      prev.filter(
        (r) => !(r.userId === row.userId && r.actionType === "REQUESTED_ADD"),
      ),
    );
    setPendingAddRoles((prev) => {
      const updated = { ...prev };
      delete updated[row.userId];
      return updated;
    });
    clearRoleSelection(row.userId);
    showNotification("Queued add removed", "info");
  };

  const handleRemoveMember = (userId) => {
    setChapterMembersLocal((prev) => prev.filter((m) => m.id !== userId));

    const originalUser = usersByLocation.find((u) => u._id === userId);

    if (originalUser) {
      setUsers((prev) => {
        const exists = prev.some((u) => u._id === originalUser._id);
        return exists ? prev : [...prev, originalUser];
      });
    }
    setPendingAddRoles((prev) => {
      const updated = { ...prev };
      delete updated[userId];
      return updated;
    });
    clearRoleSelection(userId);

    showNotification("Member removed from chapter", "info");
  };

  const usersToAddColsInTable = [
    {
      field: "userFullName",
      headerName: "Member Name",
    },
    ...(canViewChapterContact
      ? [
          {
            field: "mobileNumber",
            headerName: "Contact",
          },
        ]
      : []),
    {
      field: "role",
      headerName: "Select Role",
      renderCell: (row) => (
        <FormControl size="small" sx={{ minWidth: 150 }}>
          {/* <Select
            value={
              userRoleSelections[row.userId] ||
              pendingAddRoles[row.userId] ||
              ""
            }
            disabled={!(isCreating || canAssignMemberToChapter)}
            onChange={(e) => {
              const selectedRoleId = e.target.value;
              setUserRoleSelections((prev) => ({
                ...prev,
                [row.userId]: selectedRoleId,
              }));
              setPendingAddRoles((prev) => ({
                ...prev,
                [row.userId]: selectedRoleId,
              }));
            }}
            displayEmpty
          >
            <MenuItem value="">Choose Role</MenuItem>
            {chapterRoles.map((role) => (
              <MenuItem key={role._id} value={role._id}>
                {role.name}
              </MenuItem>
            ))}
          </Select> */}
          <Autocomplete
            size="small"
            sx={{ minWidth: 250 }}
            options={chapterRoles}
            disabled={!(isCreating || canAssignMemberToChapter)}
            value={
              chapterRoles.find(
                (role) =>
                  role._id ===
                  (userRoleSelections[row.userId] ||
                    pendingAddRoles[row.userId])
              ) || null
            }
            onChange={(event, selectedRole) => {
              const selectedRoleId = selectedRole?._id || "";

              setUserRoleSelections((prev) => ({
                ...prev,
                [row.userId]: selectedRoleId,
              }));

              setPendingAddRoles((prev) => ({
                ...prev,
                [row.userId]: selectedRoleId,
              }));
            }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) =>
              option._id === value._id
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Choose Role"
                // size="small"
                sx={{fontSize:"12px"}}
              />
            )}
          />
        </FormControl>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      renderCell: (row) => {
        const isAddedInCreate = chapterMembersLocal.some(
          (m) => m.userId === row.userId,
        );
        const isAddedInEdit = draftMemberRequests.some(
          (r) => r.userId === row.userId && r.actionType === "REQUESTED_ADD",
        );
        const isAdded = isCreating ? isAddedInCreate : isAddedInEdit;

        return (
          <Button
            size="small"
            variant={isAdded ? "outlined" : "contained"}
            color={isAdded ? "error" : "primary"}
            disabled={isEditing && !canAssignMemberToChapter}
            onClick={() =>
              isAdded
                ? handleRemoveMemberFromUserTable(row)
                : handleAddMemberFromUserTable(row)
            }
          >
            {isAdded ? "Remove" : "Add"}
          </Button>
        );
      },
    },
  ];

  const handleUserScopeChange = (event, newScope) => {
    if (!newScope) return;
    setUserScope(newScope);
    if (newScope === "all") {
      fetchUsers();
    } else {
      fetchUserByLocation();
    }
  };

  const usersToAddRowsInTable = availableUsers.map((u) => ({
    userId: u._id,
    userFullName: `${u.userFirstName} ${u.userLastName}`,
    mobileNumber: getVisibleContact(u.mobileNumber),
    rawMobileNumber: u.mobileNumber,
    role: "",
  }));

  const getPageTitle = () => {
    if (isCreating)
      return isDastakChapter ? "Create Dastak Chapter" : "Create Chapter";
    if (isEditing) return "Edit Chapter";
    return "View Chapter";
  };

  const colsInCreateChapterTable = [
    {
      field: "memberName",
      headerName: "Member Name",
    },
    ...(canViewChapterContact
      ? [
          {
            field: "contact",
            headerName: "Contact",
          },
        ]
      : []),
    {
      field: "status",
      headerName: "Status",
      renderCell: (row) => (
        <Chip
          label={row.status}
          color={row.status === "inactive" ? "warning" : "success"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      renderCell: (row) => (
        <Button
          variant="contained"
          onClick={() => {
            const alreadyAdded = selectedCreateMembers.some(
              (m) => m.id === row.id,
            );
            if (alreadyAdded) return;
            setSelectedCreateMembers((prev) => [...prev, row]);
          }}
        >
          Add
        </Button>
      ),
    },
  ];

  const rowsInCreateChapterTable = users.map((row) => ({
    id: row._id,
    memberName: row.userFirstName + " " + row.userLastName,
    contact: getVisibleContact(row.mobileNumber),
    status: row.userStatus,
  }));

  const showLegacyFormSections = !(
    isViewing &&
    isPreApprovalFlow &&
    isEditRequestView
  );

  return (
    <Paper elevation={0} variant="pageBg">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        position="relative"
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{ gap:0.5 }}
          >
            <ArrowBackIcon />
            {isMobile ? "" : "Back"}
          </Button>
        </Box>

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "1.1em", sm: "1.3em", md: "1.5em" },
            }}
          >
            {getPageTitle()}
          </Typography>
        </Box>

        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          {/* <Tooltip title="Chapter History ">

   <IconButton onClick={() => navigate(`/chapters/${id}/history`)}>
              <HistoryIcon 
                fontSize="small"
                sx={{ color: theme.palette.primary.main}}
              />
            </IconButton>
</Tooltip> */}

          {isViewing && !isPreApprovalFlow && !isMemberOnlyReview && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {canEditChapter && (
                <IconButton onClick={handleEditChapter}>
                  <EditIcon
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                </IconButton>
              )}
              {canDeleteChapter && (
                <IconButton onClick={handleDeleteChapter}>
                  <DeleteIcon
                    fontSize="small"
                    sx={{ color: theme.palette.error.main }}
                  />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Spinner loading={loading} />

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {showLegacyFormSections && (
          <>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mt: 5,
                mb: 2,
                borderBottom: "2px solid #eee",
                pb: 1,
              }}
            >
              Chapter Details
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} md={4}>
                <Controller
                  name="chapterName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Chapter Name"
                      fullWidth
                      disabled={isViewing}
                      required={isCreating}
                      InputLabelProps={{
                        sx: {
                          "& .MuiFormLabel-asterisk": {
                            color: "red",
                          },
                        },
                      }}
                      sx={{
                        ...viewModeFieldSx,
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4} md={4}>
                <Controller
                  name="chapterUniqueCode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Chapter Unique Code"
                      fullWidth
                      disabled={isViewing || isEditing}
                      sx={viewModeFieldSx}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4} md={4}>
                <Controller
                  name="chapterLifecycleStatus"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Chapter Status"
                      fullWidth
                      disabled={isViewing}
                      required={isCreating}
                      InputLabelProps={{
                        sx: {
                          "& .MuiFormLabel-asterisk": {
                            color: "red",
                          },
                        },
                      }}
                      sx={viewModeFieldSx}
                    >
                      <MenuItem value="Prospective">Prospective</MenuItem>
                      <MenuItem value="Vibrant">Vibrant</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mt: 5,
                mb: 2,
                borderBottom: "2px solid #eee",
                pb: 1,
              }}
            >
              Location Details
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <CustomSelect
                  name="state"
                  label="State"
                  control={control}
                  required={isCreating}
                  disabled={isViewing}
                  options={states.map((s) => ({
                    value: s._id,
                    label: s.stateName,
                  }))}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <CustomSelect
                  name="region"
                  label="Region"
                  control={control}
                  required={isCreating}
                  disabled={isViewing || !stateId}
                  options={regions.map((r) => ({
                    value: r._id,
                    label: r.regionName,
                  }))}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <CustomSelect
                  name="district"
                  label="District"
                  control={control}
                  required={isCreating}
                  disabled={isViewing || !stateId}
                  options={districts.map((d) => ({
                    value: d._id,
                    label: d.districtName,
                  }))}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <CustomSelect
                  name="taluka"
                  label="Taluka"
                  control={control}
                  disabled={isViewing || !districtId}
                  options={talukas.map((t) => ({
                    value: t._id,
                    label: t.talukaName,
                  }))}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <CustomSelect
                  name="village"
                  label="Village"
                  control={control}
                  disabled={isViewing || !talukaId}
                  options={villageOptions}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Controller
                  name="chapterAddress"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Chapter Address"
                      fullWidth
                      multiline
                      rows={2}
                      disabled={isViewing}
                      sx={viewModeFieldSx}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {isDastakChapter && (
              <Box sx={{ mt: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    borderBottom: "2px solid #eee",
                    pb: 1,
                  }}
                >
                  Dastak Chapter Details
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Your designation for this chapter
                      </Typography>
                      <Select
                        value={creatorDesignationId}
                        onChange={(e) => setCreatorDesignationId(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">Select designation</MenuItem>
                        {chapterRoles.map((role) => (
                          <MenuItem key={role._id} value={role._id}>
                            {role.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Chapter document
                    </Typography>
                    <input
                      ref={dastakDocumentInputRef}
                      hidden
                      type="file"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                      onChange={handleDastakDocumentChange}
                    />
                    {dastakDocument ? (
                      <Box
                        sx={{
                          width: "100%",
                          minHeight: 180,
                          border: "2px dashed #d0d5dd",
                          borderRadius: 1,
                          bgcolor: "background.paper",
                          overflow: "hidden",
                          p: 1,
                          position: "relative",
                          textAlign: "center",
                          "&:hover": {
                            borderColor: "primary.main",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            display: "flex",
                            gap: 0.5,
                            bgcolor: "rgba(255,255,255,0.92)",
                            borderRadius: 1,
                            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.16)",
                          }}
                        >
                          <Tooltip title="Preview document">
                            <IconButton
                              type="button"
                              size="small"
                              color="primary"
                              onClick={handlePreviewDastakDocument}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Replace document">
                            <IconButton
                              type="button"
                              size="small"
                              color="primary"
                              onClick={handleEditDastakDocument}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete document">
                            <IconButton
                              type="button"
                              size="small"
                              color="error"
                              onClick={handleDeleteDastakDocument}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        {dastakDocument.type?.startsWith("image/") ? (
                          <Box
                            component="img"
                            src={dastakDocumentPreviewUrl}
                            alt={dastakDocument.name}
                            sx={{
                              width: "100%",
                              height: 164,
                              objectFit: "cover",
                              borderRadius: 1,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 164,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                              px: 6,
                            }}
                          >
                            <InsertDriveFileOutlinedIcon
                              color="primary"
                              sx={{ fontSize: 42 }}
                            />
                            <Typography
                              title={dastakDocument.name}
                              noWrap
                              sx={{ maxWidth: "100%" }}
                            >
                              {dastakDocument.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Click the eye icon to preview
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box
                        component="button"
                        type="button"
                        onClick={handleEditDastakDocument}
                        sx={{
                          width: "100%",
                          minHeight: 180,
                          border: "2px dashed #d0d5dd",
                          borderRadius: 1,
                          bgcolor: "background.paper",
                          cursor: "pointer",
                          p: 1,
                          "&:hover": {
                            borderColor: "primary.main",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            height: 160,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <InsertDriveFileOutlinedIcon color="primary" />
                          <Typography>Upload Document</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Image, PDF, Word, Excel, PowerPoint or text
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}

            <Dialog
              open={dastakDocumentPreviewOpen}
              onClose={handleCloseDastakDocumentPreview}
              fullWidth
              maxWidth="md"
            >
              <DialogTitle sx={{ pr: 6 }}>
                Document Preview
                <IconButton
                  type="button"
                  aria-label="Close document preview"
                  onClick={handleCloseDastakDocumentPreview}
                  sx={{
                    position: "absolute",
                    right: 12,
                    top: 12,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent
                dividers
                sx={{
                  minHeight: { xs: 360, md: 560 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#f5f5f5",
                }}
              >
                {dastakDocument?.type?.startsWith("image/") ? (
                  <Box
                    component="img"
                    src={dastakDocumentPreviewUrl}
                    alt={dastakDocument?.name || "Document preview"}
                    sx={{
                      display: "block",
                      maxWidth: "100%",
                      maxHeight: "70vh",
                      objectFit: "contain",
                    }}
                  />
                ) : dastakDocument?.type === "application/pdf" ||
                  dastakDocument?.type?.startsWith("text/") ? (
                  <Box
                    component="iframe"
                    title={dastakDocument?.name || "Document preview"}
                    src={dastakDocumentPreviewUrl}
                    sx={{
                      width: "100%",
                      height: "70vh",
                      border: 0,
                      bgcolor: "white",
                    }}
                  />
                ) : (
                  <Box sx={{ textAlign: "center", p: 3 }}>
                    <Typography sx={{ mb: 2 }}>
                      Preview is not available for this file type.
                    </Typography>
                    <Button
                      variant="outlined"
                      href={dastakDocumentPreviewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open Document
                    </Button>
                  </Box>
                )}
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* {
          isCreating && (
            <>
              <Box sx={{ my: 4 }}>

                <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mt: 1,
                      mb: 2,
                      borderBottom: "2px solid #eee",
                      pb: 1,
                    }}
                  >
                    Chapter Members TEST
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ height: "max-content" }}
                    disabled={!talukaId || !districtId}
                    onClick={() => {
                      fetchUserByLocation()
                      setShowAddMemberTable(true);
                    }}
                  >
                    Find Members by Location TEST
                    {/* Find Members by {districtId ? districts.districtName : talukaId ? talukas.talukaName : "Location" } TEST 
                  </Button>
                </div>

                <ReactTable
                  // columns={colsInCreateChapterTable}
                  // rows={selectedCreateMembers}
                  columns={colsInChapterTable}
                  rows={rowsInChapterTable}
                />

              </Box>
              {showAddMemberTable && <Box sx={{ my: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mt: 1,
                    mb: 2,
                    borderBottom: "2px solid #eee",
                    pb: 1,
                  }}
                >
                  All Members TEST
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => handleSubmitAll()}
                  disabled={!selectedMembers.length || loading}
                >
                  {isCreating ? "Add Members to Chapter" : isEditing ? "Add Selected Members" : ""}
                </Button>
                <ReactTable
                  columns={usersToAddColsInTable}
                  rows={usersToAddRowsInTable}
                // columns={colsInCreateChapterTable}
                // rows={rowsInCreateChapterTable}
                />
              </Box>}
            </>
          )
        } */}

        <Box
          sx={{
            display: "flex",
            justifyContent: shouldShowChapterMembersTable
              ? "space-between"
              : "flex-end",
            alignItems: "center",
            my: 1,
            gap: 2,
          }}
        >
          {shouldShowChapterMembersTable && !isUnifiedReviewMode && (
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mt: 1,
                mb: 2,
                borderBottom: "2px solid #eee",
                pb: 1,
              }}
            >
              Chapter Members
            </Typography>
          )}
          {isEditing &&
            !isCreating &&
            !showAddMemberTable &&
            canAssignMemberToChapter && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    fetchUserByLocation();
                    setShowAddMemberTable(true);
                    setSelectedUserForMember(null);
                    setUserRoleSelections({});
                  }}
                >
                  + Add Member
                </Button>
              </>
            )}
          {isCreating && !isEditing && (
            // {isCreating && !isEditing && canAssignMemberToChapter && (

            <Box sx={{ display: "flex", gap: 1 }}>
              {!showCreateMemberSection ? (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setShowCreateMemberSection(true);
                    fetchUserByLocation();
                    setShowAddMemberTable(true);
                  }}
                >
                  Add Members Now
                </Button>
              ) : (
                <>
                  {/* <Button
                    variant="contained"
                    size="small"
                    sx={{ height: "max-content" }}
                    disabled={!districtId && !talukaId}
                    onClick={() => {
                      fetchUserByLocation();
                      setShowAddMemberTable(true);
                    }}
                  >
                    Find Members
                  </Button> */}
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setShowCreateMemberSection(false);
                      setShowAddMemberTable(false);
                    }}
                  >
                    Assign Members Later
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>

        {shouldShowChapterMembersTable &&
          !isUnifiedReviewMode &&
          (!isCreating || showCreateMemberSection) && (
            <Box sx={{ mb: 4 }}>
              <ReactTable
                columns={colsInChapterTable}
                rows={rowsInChapterTable}
                rowKey="_id"
                initialOrderBy=""
              />
            </Box>
          )}

        {(isEditing || (isCreating && showCreateMemberSection)) &&
          showAddMemberTable && (
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  my: 1,
                  gap: 2,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "35px",
                    alignItems: "center",
                    justifyContent: "space-around",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      borderBottom: "2px solid #eee",
                    }}
                  >
                    Choose members from
                    {/* {isCreating ? "All Members" : isEditing ? "Available Members" : ""}                    <InfoOutlined/> */}
                  </Typography>
                  <Typography variant="subtitle" sx={{ mb: 1 }}>
                    &nbsp;
                    <ToggleButtonGroup
                      value={userScope}
                      exclusive
                      onChange={handleUserScopeChange}
                      size="small"
                      sx={{ verticalAlign: "middle" }}
                    >
                      <ToggleButton value="district" disabled={!districtId}>
                        District Level
                      </ToggleButton>

                      <ToggleButton value="all">All Users</ToggleButton>
                    </ToggleButtonGroup>
                  </Typography>
                </div>
                <div
                  style={{ display: "flex", flexDirection: "row", gap: "10px" }}
                >
                  {/* <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setSelectedUserForMember(null);
                      setUserRoleSelections({});
                      setShowAddMemberTable(false)
                    }}
                  >
                    Done
                  </Button> */}
                </div>
              </Box>

              <ReactTable
                columns={usersToAddColsInTable}
                rows={usersToAddRowsInTable}
                rowKey="userId"
              />
            </Box>
          )}
        {/* </> */}
        {/* )} */}

        {/* {((isCreating && canCreateChapter) || (isEditing && canEditChapter)) && ( */}
        {(isCreating || isEditing) && (
          <Box
            mt={3}
            sx={{ display: "flex", justifyContent: "flex-end" }}
            gap={2}
          >
            <CustomButton loading={loading} type="submit" variant="contained">
              {isCreating ? "Create Chapter" : "Update Chapter"}
            </CustomButton>
            {/* {isEditing && (
              <Button variant="outlined" color="error" onClick={handleBack}>
                Cancel
              </Button>
            )} */}
          </Box>
        )}

        {isUnifiedReviewMode && (
          <Box mt={3}>
            {isVerifyEditRequestView ? (
              chapterEditDiffs.length > 0 ? (
                <>
                  {["Chapter Information", "Location Information"].map(
                    (group) => {
                      const groupRows = chapterEditDiffs.filter(
                        (r) => r.group === group,
                      );
                      if (!groupRows.length) return null;

                      return (
                        <Box key={group} sx={{ mb: 3 }}>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            {group}
                          </Typography>
                          <Box
                            sx={{
                              borderBottom: "1px solid #D0D7DE",
                              mb: 2,
                            }}
                          />
                          <Grid container spacing={2}>
                            {groupRows.map((row) => (
                              <Grid
                                item
                                xs={12}
                                md={4}
                                key={`${group}-${row.label}`}
                              >
                                <Box
                                  sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: row.changed
                                      ? "#FFE58F"
                                      : "#E5E7EB",
                                    backgroundColor: row.changed
                                      ? "#FFFBE6"
                                      : "#FFFFFF",
                                    minHeight: 88,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={600}
                                  >
                                    {row.label}
                                  </Typography>

                                  {row.changed ? (
                                    <Typography sx={{ mt: 1 }}>
                                      <span
                                        style={{
                                          color: "#9CA3AF",
                                          textDecoration: "line-through",
                                        }}
                                      >
                                        {row.previous || "-"}
                                      </span>
                                      <span style={{ margin: "0 8px" }}>
                                        {"->"}
                                      </span>
                                      <span
                                        style={{
                                          color: "#15803D",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {row.current || "-"}
                                      </span>
                                      <Chip
                                        label="Updated"
                                        size="small"
                                        sx={{ ml: 1, height: 22 }}
                                      />
                                    </Typography>
                                  ) : (
                                    <Typography sx={{ mt: 1 }}>
                                      {row.current || "-"}
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      );
                    },
                  )}
                </>
              ) : (
                <Typography color="text.secondary">
                  No field-level differences detected.
                </Typography>
              )
            ) : null}

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Chapter Member Changes
              </Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #D0D7DE",
                  mb: 2,
                }}
              />

              {memberChangeDiffs.length > 0 ? (
                <Grid container spacing={2}>
                  {memberChangeDiffs.map((row) => (
                    <Grid item xs={12} md={4} key={row.id}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "#FFE58F",
                          backgroundColor: "#FFFBE6",
                          minHeight: 108,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            {row.memberName}
                          </Typography>
                          <Chip
                            label={row.action}
                            size="small"
                            color="warning"
                            sx={{ maxWidth: 170 }}
                          />
                        </Box>

                        <Typography variant="caption" color="text.secondary">
                          {row.mobile}
                        </Typography>

                        <Typography sx={{ mt: 1 }}>
                          <span
                            style={{
                              color: "#9CA3AF",
                              textDecoration: "line-through",
                            }}
                          >
                            {row.previous || "-"}
                          </span>
                          <span style={{ margin: "0 8px" }}>{"->"}</span>
                          <span style={{ color: "#15803D", fontWeight: 600 }}>
                            {row.current || "-"}
                          </span>
                          <Chip
                            label="Updated"
                            size="small"
                            sx={{ ml: 1, height: 22 }}
                          />
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography color="text.secondary">
                  No member-level differences detected.
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {isViewing && (isPreApprovalFlow || isMemberOnlyReview) && (
          <Box mt={3} display="flex" gap={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="error"
              disabled={loading}
              // onClick={() =>
              //   isMemberOnlyReview
              //     ? handleMemberOnlyVerification(false)
              //     : handleChapterVerification(false)
              // }
              onClick={() => {
                setConfirmAction("decline");
                setDeclineReason("");
                setDeclineReasonError("");
                setConfirmDialogOpen(true);
              }}
            >
              Decline
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={loading}
              // onClick={() =>
              //   isMemberOnlyReview
              //     ? handleMemberOnlyVerification(true)
              //     : handleChapterVerification(true)
              // }
              onClick={() => {
                setConfirmAction("accept");
                setDeclineReason("");
                setDeclineReasonError("");
                setConfirmDialogOpen(true);
              }}
            >
              Accept
            </Button>
          </Box>
        )}
      </Box>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => {
          setConfirmDialogOpen(false);
          setDeclineReason("");
          setDeclineReasonError("");
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            minWidth: 380,
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: "600" }}>
            {confirmAction === "accept" ? "Confirm Accept" : "Confirm Decline"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to{" "}
            {confirmAction === "accept" ? "accept" : "decline"} this chapter
            request?
          </DialogContentText>
          {confirmAction === "decline" && (
            <TextField
              fullWidth
              multiline
              minRows={3}
              margin="dense"
              label="Rejection Reason"
              value={declineReason}
              onChange={(e) => {
                setDeclineReason(e.target.value);
                if (declineReasonError) setDeclineReasonError("");
              }}
              error={Boolean(declineReasonError)}
              helperText={
                declineReasonError || "Reason is required when declining."
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false);
              setDeclineReason("");
              setDeclineReasonError("");
            }}
            color="primary"
          >
            No
          </Button>
          <Button
            onClick={() => {
              const trimmedReason = declineReason.trim();
              if (confirmAction === "decline" && !trimmedReason) {
                setDeclineReasonError("Please enter rejection reason.");
                return;
              }

              setConfirmDialogOpen(false);
              setDeclineReason("");
              setDeclineReasonError("");
              if (confirmAction === "accept") {
                isMemberOnlyReview
                  ? handleMemberOnlyVerification(true, "")
                  : handleChapterVerification(true, "");
              } else {
                isMemberOnlyReview
                  ? handleMemberOnlyVerification(false, trimmedReason)
                  : handleChapterVerification(false, trimmedReason);
              }
            }}
            color={confirmAction === "accept" ? "primary" : "error"}
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Chapter</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this chapter? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteChapter}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: "middle", horizontal: "center" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            minWidth: "300px",
            borderRadius: "8px",
          },
        }}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={closeNotification}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: "8px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ChapterDetailsPage;



